import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point, polygon } from "@turf/turf";

import { XYLocation } from "@nens/deck-gl";

import { getV3Api } from "../apiConfiguration";
import { AppState, Thunk } from "../App";
import { Asset } from "../types/assets";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import {
  SET as SELECTED_ASSET_WAS_SET,
  Set as SelectedAssetWasSet,
  WaterLevelChart,
} from "./selectedAsset";
import { getGridcells } from "./simulation";
import {
  Websocket,
  closeWebsocket,
  openRainGraphOnPointWebsocket,
  openWaterlevelWebsocket,
} from "./websockets";

interface SelectedAsset {
  xy: XYLocation | null;
  demValue: number | null;
  gridcell: Asset | null;
  groundwaterGridcell: Asset | null;
  id: string | null;
  waterLevelChart: WaterLevelChart;
  groundwaterlevelChart: WaterLevelChart;
  rainFallLevelChart: WaterLevelChart;
}
const INITIAL_STATE: SelectedAsset = {
  xy: null,
  demValue: null,
  gridcell: null,
  groundwaterGridcell: null,
  id: null,
  waterLevelChart: [],
  groundwaterlevelChart: [],
  rainFallLevelChart: [],
};

// Action types
const SETXY = "selectedPoint/SETXY";
const REMOVEXY = "selectedPoint/REMOVEXY";
const SET_DEM_VALUE = "selectedPoint/SET_DEM_VALUE";
const SET_GRID_CELL = "selectedPoint/SET_GRID_CELL";
const SET_GROUNDWATER_GRID_CELL = "selectedPoint/SET_GROUNDWATER_GRID_CELL";
const ADD_WATERLEVELCHART_DATA = "selectedPoint/ADD_WATERLEVELCHART_DATA";
const ADD_GROUNDWATERLEVELCHART_DATA = "selectedPoint/ADD_GROUNDWATERLEVELCHART_DATA";
const ADD_RAINFALLLEVELCHART_DATA = "selectedPoint/ADD_RAINFALLLEVELCHART_DATA";
export const getXY = (state: AppState) => state.selectedPoint.xy;
export const getDemValue = (state: AppState) => state.selectedPoint.demValue;
export const getGridcell = (state: AppState) => state.selectedPoint.gridcell;
export const getGroundwaterGridcell = (state: AppState) => state.selectedPoint.groundwaterGridcell;
export const getWaterLevelChart = (state: AppState): WaterLevelChart =>
  state.selectedPoint.waterLevelChart;
export const getGroundwaterLevelChart = (state: AppState): WaterLevelChart =>
  state.selectedPoint.groundwaterlevelChart;
export const getRainFallLevelChart = (state: AppState): WaterLevelChart =>
  state.selectedPoint.rainFallLevelChart;

interface SetXY {
  type: typeof SETXY;
  xy: XYLocation;
}
interface RemoveXY {
  type: typeof REMOVEXY;
}
interface SetDemValue {
  type: typeof SET_DEM_VALUE;
  value: number;
}
interface SetGridCells {
  type: typeof SET_GRID_CELL;
  gridcell: Asset;
}
interface SetGroundwaterGridCells {
  type: typeof SET_GROUNDWATER_GRID_CELL;
  groundwaterGridcell: Asset;
}

interface AddWaterlevelchartData {
  type: typeof ADD_WATERLEVELCHART_DATA;
  data: WaterLevelChart;
}
interface AddGroundwatterlevelchartData {
  type: typeof ADD_GROUNDWATERLEVELCHART_DATA;
  data: WaterLevelChart;
}
interface AddRainFallLevelchartData {
  type: typeof ADD_RAINFALLLEVELCHART_DATA;
  data: WaterLevelChart;
}
type SelectedPointAction =
  | SetXY
  | RemoveXY
  | SetDemValue
  | SetGridCells
  | SetGroundwaterGridCells
  | AddWaterlevelchartData
  | AddGroundwatterlevelchartData
  | AddRainFallLevelchartData
  | BackToStartScreen
  | SelectedAssetWasSet;

export const setXY =
  (xy: XYLocation): Thunk =>
  async (dispatch, getState) => {
    dispatch({
      type: SETXY,
      xy,
    });
    const state = getState();
    const v3Api = getV3Api();
    const lineEWKT = `LINESTRING( ${xy[0]} ${xy[1]} , ${xy[0]} ${xy[1]} )`;
    const gridcells = getGridcells(state);
    const pt = point(xy);

    if (gridcells && gridcells.features) {
      const clickedSurfacewaterGridcell = [...gridcells.features]
        .filter(
          (gridcell) => gridcell && gridcell.properties && gridcell.properties.node_type === 1
        )
        .find((gridcell) => {
          // find fridcell that points falls intoo
          // https://www.npmjs.com/package/@turf/boolean-point-in-polygon
          // typescript thinks coordinates does not always exist on gridcell.geometry
          if (gridcell.geometry === null || gridcell.geometry.type !== "Polygon") {
            return false;
          } else {
            const poly = polygon([...gridcell.geometry.coordinates]);
            return booleanPointInPolygon(pt, poly);
          }
        });
      const clickedGroundwaterGridcell = [...gridcells.features]
        .filter(
          (gridcell) => gridcell && gridcell.properties && gridcell.properties.node_type === 2
        )
        .find((gridcell) => {
          // find fridcell that points falls intoo
          // https://www.npmjs.com/package/@turf/boolean-point-in-polygon
          // typescript thinks coordinates does not always exist on gridcell.geometry
          if (gridcell.geometry === null || gridcell.geometry.type !== "Polygon") {
            return false;
          } else {
            const poly = polygon([...gridcell.geometry.coordinates]);
            return booleanPointInPolygon(pt, poly);
          }
        });

      if (clickedSurfacewaterGridcell && clickedSurfacewaterGridcell.properties) {
        // clickedGridcell is not an asset. We need to fix this in https://nelen-schuurmans.atlassian.net/browse/FRNT-907
        //@ts-ignore
        dispatch(setGridCells(clickedSurfacewaterGridcell));
        dispatch(
          openWaterlevelWebsocket(clickedSurfacewaterGridcell, Websocket.WaterlevelChartPoint)
        );
        dispatch(
          openRainGraphOnPointWebsocket(
            clickedSurfacewaterGridcell,
            Websocket.RainGraphOnPointChart
          )
        );
      }
      if (clickedGroundwaterGridcell && clickedGroundwaterGridcell.properties) {
        //@ts-ignore
        dispatch(setGroundwaterGridcell(clickedGroundwaterGridcell));
        dispatch(
          openWaterlevelWebsocket(clickedGroundwaterGridcell, Websocket.GroundWaterChartPoint)
        );
      }
    }

    try {
      const simulation = getState().simulation.simulation;
      if (simulation === null) return;
      const profileSimulationId = simulation.id;
      if (profileSimulationId === undefined) return;
      const profiles = await v3Api.simulationsVisualisationsProfileList({
        simulationPk: profileSimulationId,
        layer: "dem",
      });
      const { url } = profiles;
      const response = await fetch(url.replace("{line}", lineEWKT).replace("{samples}", "2"));
      const values: number[] = await response.json();
      dispatch({
        type: SET_DEM_VALUE,
        value: values[0],
      });
    } catch (error) {
      console.error("Caught error wait for dem profile:", error);
      return;
    }
  };

export const removeXY = (): Thunk => (dispatch) => {
  dispatch({
    type: REMOVEXY,
  });
  // Close current active WaterlevelChartPoint and GroundWaterChartPoint websockets
  dispatch(closeWebsocket(Websocket.WaterlevelChartPoint));
  dispatch(closeWebsocket(Websocket.GroundWaterChartPoint));
  dispatch(closeWebsocket(Websocket.RainGraphOnPointChart));
};

export const setGridCells = (gridcell: Asset): SelectedPointAction => {
  return {
    type: SET_GRID_CELL,
    gridcell,
  };
};

export const setGroundwaterGridcell = (groundwaterGridcell: Asset) => {
  return {
    type: SET_GROUNDWATER_GRID_CELL,
    groundwaterGridcell,
  };
};

export const addWaterlevelchartData = (data: WaterLevelChart) => {
  return {
    type: ADD_WATERLEVELCHART_DATA,
    data: data,
  };
};
export const addGroundwatterlevelchartData = (data: WaterLevelChart) => {
  return {
    type: ADD_GROUNDWATERLEVELCHART_DATA,
    data: data,
  };
};
export const addRainFalllevelchartData = (data: WaterLevelChart) => {
  return {
    type: ADD_RAINFALLLEVELCHART_DATA,
    data: data,
  };
};

export default function reducer(state = INITIAL_STATE, action: SelectedPointAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SELECTED_ASSET_WAS_SET:
      return INITIAL_STATE;
    case REMOVEXY:
      return INITIAL_STATE;
    case SETXY: {
      const { xy } = action;
      return {
        ...state,
        xy: xy,
        id: null,
        gridcell: null,
        waterLevelChart: [],
        groundwaterlevelChart: [],
      };
    }

    case SET_DEM_VALUE: {
      const { value } = action;
      return {
        ...state,
        demValue: value,
      };
    }
    case SET_GRID_CELL: {
      const { gridcell } = action;
      return {
        ...state,
        gridcell,
      };
    }
    case SET_GROUNDWATER_GRID_CELL: {
      const { groundwaterGridcell } = action;
      return {
        ...state,
        groundwaterGridcell,
      };
    }
    case ADD_WATERLEVELCHART_DATA: {
      return {
        ...state,
        waterLevelChart: [...state.waterLevelChart, ...action.data],
      };
    }
    case ADD_GROUNDWATERLEVELCHART_DATA: {
      return {
        ...state,
        groundwaterlevelChart: [...state.groundwaterlevelChart, ...action.data],
      };
    }
    case ADD_RAINFALLLEVELCHART_DATA: {
      return {
        ...state,
        rainFallLevelChart: [...state.rainFallLevelChart, ...action.data],
      };
    }
    default:
      return state;
  }
}
